import { useState, useCallback, memo, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpotify,
  faExternalLinkAlt,
} from "@fortawesome/free-brands-svg-icons";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { useWidgetSettings } from "../WidgetSettings";

// Helper function to extract Spotify ID from various URL formats
const extractSpotifyId = (url) => {
  if (!url) return null;

  // Handle Spotify URI format (spotify:type:id)
  const uriMatch = url.match(
    /spotify:(track|playlist|album|artist):([a-zA-Z0-9]+)/
  );
  if (uriMatch) {
    return {
      type: uriMatch[1],
      id: uriMatch[2],
    };
  }

  // Handle Spotify URL format (open.spotify.com/type/id)
  const urlMatch = url.match(
    /open\.spotify\.com\/(track|playlist|album|artist)\/([a-zA-Z0-9]+)/
  );
  if (urlMatch) {
    return {
      type: urlMatch[1],
      id: urlMatch[2],
    };
  }

  // Handle direct ID input - try to determine type based on length and format
  const directIdMatch = url.match(/^([a-zA-Z0-9]+)$/);
  if (directIdMatch) {
    const id = directIdMatch[1];
    // Spotify playlist IDs often start with '37i' or are longer than 22 characters
    if (id.startsWith("37i") || id.length > 22) {
      return { type: "playlist", id };
    }
    // Default to track for shorter IDs
    return { type: "track", id };
  }

  return null;
};

const SpotifyConfigForm = memo(function SpotifyConfigForm({
  config,
  onUpdate,
}) {
  const { setActionButton, clearActionButton } = useWidgetSettings();

  const [formData, setFormData] = useState({
    spotifyUrl: config.spotifyUrl || "",
    showLiveListening: config.showLiveListening || false,
    isAuthenticated: config.isAuthenticated || false,
    spotifyAccessToken: config.spotifyAccessToken || "",
    refreshToken: config.refreshToken || "",
    userProfile: config.userProfile || null,
    fullBackgroundArt: config.fullBackgroundArt || false,
  });

  const [urlError, setUrlError] = useState("");
  const [urlValidated, setUrlValidated] = useState(false);

  // Add handler to stop propagation
  const stopPropagation = useCallback((e) => {
    if (e) {
      e.stopPropagation();
      e.nativeEvent?.stopImmediatePropagation();
    }
  }, []);

  const validateSpotifyUrl = useCallback((url) => {
    if (!url.trim()) {
      setUrlError("");
      setUrlValidated(false);
      return false;
    }

    const spotifyData = extractSpotifyId(url);
    if (!spotifyData) {
      setUrlError(
        "Please enter a valid Spotify URL (track, playlist, album, or artist)"
      );
      setUrlValidated(false);
      return false;
    }

    setUrlError("");
    setUrlValidated(true);
    return true;
  }, []);

  const handleUrlChange = useCallback(
    (value) => {
      setFormData((prev) => ({ ...prev, spotifyUrl: value }));
      validateSpotifyUrl(value);
    },
    [validateSpotifyUrl]
  );

  const handleSubmit = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      // Validate URL if provided
      if (formData.spotifyUrl && !validateSpotifyUrl(formData.spotifyUrl)) {
        return;
      }

      // Update the widget config
      onUpdate(formData);
    },
    [formData, onUpdate, validateSpotifyUrl]
  );

  // Set up action button
  useEffect(() => {
    const actionButton = (
      <button
        type="button"
        onClick={handleSubmit}
        className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
      >
        <FontAwesomeIcon icon={faSpotify} className="w-4 h-4" />
        Save Spotify Widget
      </button>
    );

    setActionButton(actionButton);

    return () => {
      clearActionButton();
    };
  }, [setActionButton, clearActionButton, handleSubmit]);

  const getPreviewInfo = () => {
    if (!formData.spotifyUrl) return null;

    const spotifyData = extractSpotifyId(formData.spotifyUrl);
    if (!spotifyData) return null;

    const typeLabels = {
      track: "Track",
      playlist: "Playlist",
      album: "Album",
      artist: "Artist",
    };

    return {
      type: typeLabels[spotifyData.type] || "Unknown",
      id: spotifyData.id,
    };
  };

  const previewInfo = getPreviewInfo();

  return (
    <div
      className="space-y-6"
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
    >
      {/* Spotify URL Input */}
      <div className="space-y-3">
        <Label className="text-white font-medium">Spotify URL</Label>
        <Input
          value={formData.spotifyUrl}
          onChange={(e) => {
            stopPropagation(e);
            handleUrlChange(e.target.value);
          }}
          placeholder="https://open.spotify.com/track/..."
          className="bg-white/5 border-white/10 text-white placeholder:text-white/50 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-white/20 break-all whitespace-normal"
          style={{
            wordBreak: "break-all",
            whiteSpace: "normal",
            lineHeight: "1.4",
            minHeight: "2.5rem",
            height: "auto",
            padding: "0.75rem",
          }}
        />

        {urlError && (
          <p className="text-sm text-red-400 flex items-center gap-2">
            <FontAwesomeIcon icon={faInfoCircle} className="w-4 h-4" />
            {urlError}
          </p>
        )}

        {urlValidated && previewInfo && (
          <div className="text-sm text-green-400 flex items-center gap-2">
            <FontAwesomeIcon icon={faSpotify} className="w-4 h-4" />
            <span>Valid {previewInfo.type} detected</span>
          </div>
        )}
      </div>

      {/* Full Background Art Toggle */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-white font-medium">
              Full Background Art
            </Label>
            <p className="text-xs text-white/60 mt-1">
              Use album/playlist art as full widget background
            </p>
          </div>
          <Switch
            checked={formData.fullBackgroundArt}
            onCheckedChange={(checked) => {
              const newFormData = { ...formData, fullBackgroundArt: checked };
              setFormData(newFormData);
              // Update widget immediately
              onUpdate(newFormData);
            }}
          />
        </div>
      </div>

      {/* Spotify Account Connection - Hidden for now */}
      {false && (
        <div className="space-y-4">
          <Label className="text-white font-medium">Spotify Account</Label>
          {/* Account connection UI hidden */}
        </div>
      )}
    </div>
  );
});

SpotifyConfigForm.displayName = "SpotifyConfigForm";

export default SpotifyConfigForm;
